import React from 'react';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';

import { DropdownMenuType, DropdownMenuTypePropType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';

import dropdownMenuChildValidator from './dropdown-menu-child-validator';
import { AriaWidgetRole } from '../../../constants/aria-widget-roles';
import noop from '../../default-props/noop';
import DropdownMenuPosition from '../../prop-types/dropdown-menu-position';
import { closestElement } from '../../services/closest-service';
import generateDataTestidAttribute from '../../services/testid-service';
import DropdownMenuItemGroup from '../dropdown-menu-item-group';
import DropdownMenuToggler from '../dropdown-menu-toggler';
import InlineMessage, { InlineMessageTypePropType } from '../inline-message';
import Dropdown from '../INTERNAL/dropdown';

import style from './dropdown-menu.less';

const TYPEAHEAD_TIMEOUT = 300;
const dropdownMenuTogglerType = (<DropdownMenuToggler />).type;
const dropdownMenuItemGroupType = (
  <DropdownMenuItemGroup index={0} label=''>
    <div />
  </DropdownMenuItemGroup>
).type;

const callbackFunctionDecorator = createSelector(
  (decoratingFn) => decoratingFn,
  (_, decoratedFn) => decoratedFn,
  (decoratingFn, decoratedFn) => {
    return (...args) => {
      decoratedFn?.(...args);
      decoratingFn(...args);
    };
  },
);

class DropdownMenu extends React.PureComponent {
  static propTypes = {
    'data-testid': PropTypes.string,
    ariaLabel: PropTypes.string,
    children: dropdownMenuChildValidator.isRequired,
    className: PropTypes.string,
    closeWhenChildrenClicked: PropTypes.bool,
    containerAllowedMeToClose: PropTypes.func,
    disabled: PropTypes.bool,
    disabledTooltipMessage: PropTypes.node,
    dropdownMenuClassName: PropTypes.string,
    dropdownMenuShouldHaveWidthOfButton: PropTypes.bool,
    fullWidth: PropTypes.bool,
    fullHeight: PropTypes.bool,
    hasError: PropTypes.bool,
    inlineMessage: PropTypes.node,
    inlineMessageType: InlineMessageTypePropType,
    inlineMessageTooltip: PropTypes.node,
    id: PropTypes.string,
    menuPosition: DropdownMenuPosition,
    onBlur: PropTypes.func,
    onClose: PropTypes.func,
    onFocus: PropTypes.func,
    onOpen: PropTypes.func,
    variant: PropTypes.oneOf(['button', 'form']),
    stopScrollPropagation: PropTypes.bool,
    dropdownMenuType: DropdownMenuTypePropType,
  };

  static defaultProps = {
    'data-testid': undefined,
    ariaLabel: undefined,
    className: '',
    closeWhenChildrenClicked: true,
    containerAllowedMeToClose: undefined,
    disabled: false,
    disabledTooltipMessage: undefined,
    dropdownMenuClassName: '',
    dropdownMenuShouldHaveWidthOfButton: false,
    fullWidth: false,
    fullHeight: false,
    hasError: false,
    inlineMessage: '',
    inlineMessageType: '',
    inlineMessageTooltip: undefined,
    id: '',
    menuPosition: 'bottom-left',
    onBlur: undefined,
    onClose: noop,
    onFocus: undefined,
    onOpen: noop,
    variant: 'button',
    stopScrollPropagation: false,
    dropdownMenuType: undefined,
  };

  constructor(...args) {
    super(...args);

    this._dropdownRef = React.createRef();
  }

  state = {
    isFocused: false,
    isOpen: false,
    rowHovered: null,
    searchString: '',
    searchTimeout: null,
  };

  componentWillUnmount() {
    this._clearTimeout();
  }

  _clearSearchString() {
    if (this.state.searchString) {
      this.setState({
        searchString: '',
      });
    }
  }

  _onScrollPropogationHandler(e) {
    if (this.props.stopScrollPropagation) {
      e.stopPropagation();
    }
  }

  _clearTimeout() {
    const { searchTimeout } = this.state;

    if (searchTimeout) {
      clearTimeout(searchTimeout);
      this.setState({ searchTimeout: null });
    }
  }

  _getChildText = (child) => {
    if (typeof child === 'string') {
      if (child === ' ') {
        return '';
      }

      return child.toLowerCase();
    }

    if (typeof child === 'number') {
      return String(child);
    }

    if (child && child.props && child.props.children) {
      return React.Children.map(child.props.children, this._getChildText)
        .filter((str) => !!str)
        .join(' ');
    }

    return '';
  };

  _getMatchingChildIndex(searchString) {
    if (!searchString) {
      return null;
    }

    const lowercased = searchString.toLowerCase();

    return this._getMenuItemsWithoutGroups().reduce((index, item, i) => {
      if (index !== null) {
        return index;
      }

      if (!item.props.disabled && this._getChildText(item).indexOf(lowercased) === 0) {
        return i;
      }

      return null;
    }, null);
  }

  _onClose = () => {
    // Reset rowHovered
    this._onRowHover();
    this.setState({ isOpen: false });
    this.props.onClose();
  };

  _getMenuItems(children = this.props.children) {
    return React.Children.toArray(children).filter((child) => !!child && child.type !== dropdownMenuTogglerType);
  }

  _getMenuItemsWithoutGroups() {
    return this._getMenuItems().reduce((acc, menuItemOrGroup) => {
      if (menuItemOrGroup.type !== dropdownMenuItemGroupType) {
        acc.push(menuItemOrGroup);
        return acc;
      }

      return acc.concat(this._getMenuItems(menuItemOrGroup.props.children));
    }, []);
  }

  _getMenuToggler() {
    return React.Children.toArray(this.props.children).find((child) => child?.type === dropdownMenuTogglerType);
  }

  /*
   * This function helps find the next safe index to move to, while respecting disabled status.
   * The math works out like this: Take the incoming *target* index we'd *like* to move to, and
   * split the array on that index, reordering it so that's in the first position, e.g. given:
   * [0, 1, 2, 3, 4, 5]
   * and targeting index 4, we would wind up with this reorderedMenuItems array:
   * [4, 5, 0, 1, 2, 3]
   * Then run a .findIndex operation from the beginning of the array, with our predicate function
   * checking the disabled status of each item, so we grab the first valid item starting at the
   * target index. If we can't find any, we'll get back a -1 and return null, and if we get back
   * a valid index, we need to shift it by 'targetIndex' slots and then mod it against the array's
   * length to determine what the actual index will be against the original array. E.g. if we're
   * targeting index 4, and find that value '1' is the next actual valid item, then in the shifted
   * array, value '1' is in index 3, so add the original 'targetIndex' to shift to the position in
   * the original array (3 + 4 = 7) and then mod it by the array's length ((3 + 4) % 6 = 1), and
   * we should wind up with the index for value '1' in the original array, which is index 1.
   */
  _getNextEnabledIndex(targetIndex) {
    const menuItems = this._getMenuItemsWithoutGroups();
    const reorderedMenuItems = [...menuItems.slice(targetIndex), ...menuItems.slice(0, targetIndex)];
    const nextEnabledIndex = reorderedMenuItems.findIndex((item) => !item.props.disabled);

    if (nextEnabledIndex === -1) {
      return null;
    }

    return (targetIndex + nextEnabledIndex) % menuItems.length;
  }

  /*
   * This function helps find the previous safe index to move to, while respecting disabled status.
   * The math works out like this: Take the incoming *target* index we'd *like* to move to, and
   * split the array on the next index, reordering it so that's in the last position, and then run
   * a reverse on the output array, so we can run a .findIndex to find the *previous* index, e.g.
   * given:
   * [0, 1, 2, 3, 4, 5]
   * and targeting index 4, we would split on the next item, and get this on the initial reorder:
   * [5, 0, 1, 2, 3, 4]
   * and then reversing that will give us the actual reorderedMenuItems array:
   * [4, 3, 2, 1, 0, 5]
   * Then run a .findIndex operation from the beginning of the array, with our predicate function
   * checking the disabled status of each item, so we grab the first valid item starting at the
   * target index. If we can't find any, we'll get back a -1 and return null, and if we get back
   * a valid index, we need to shift it by 'array.length - reversedPreviousEnabledIndex' slots, to
   * account for the fact that we reversed the array, and then add the original 'targetIndex' value,
   * and mod it against the array's length to determine what the actual index will be against the
   * original array. E.g. if we're targeting index 4, and find that value '1' is the next actual
   * valid item, then in the shifted array, value '1' is in index 3 of the reversed array, so first,
   * get the index of what it would be in the original split array, which would be its reversed index
   * subtracted from the array length (6 - 3 = 3), add the original 'targetIndex' to shift to the
   * position in the original unsplit/reordered array (3 + 4 = 7) and then mod it by the array's
   * length ((3 + 4) % 6 = 1), and we should wind up with the index for value '1' in the original
   * array, which is index 1.
   */
  _getPreviousEnabledIndex(targetIndex) {
    const menuItems = this._getMenuItemsWithoutGroups();
    const reorderedMenuItems = [...menuItems.slice(targetIndex + 1), ...menuItems.slice(0, targetIndex + 1)].reverse();
    const reversedPreviousEnabledIndex = reorderedMenuItems.findIndex((item) => !item.props.disabled);

    if (reversedPreviousEnabledIndex === -1) {
      return null;
    }

    return (menuItems.length - reversedPreviousEnabledIndex + targetIndex) % menuItems.length;
  }

  _isFocused = ({ relatedTarget, target, type }) => {
    const { overlay, toggler } = this._dropdownRef.current;

    return (
      (type === 'focus' &&
        ((toggler.current && closestElement(target, toggler.current)) || (overlay.current && closestElement(target, overlay.current)))) ||
      (type === 'blur' &&
        ((toggler.current && closestElement(relatedTarget, toggler.current)) ||
          (overlay.current && closestElement(relatedTarget, overlay.current))))
    );
  };

  _onBlur = (e) => {
    if (this.state.isFocused && !this._isFocused(e)) {
      if (this.state.isOpen) {
        e.preventDefault();
        this._onClose();
      }

      this.props.onBlur?.(e);
      this.setState({ isFocused: false });
    }
  };

  _onFocus = (e) => {
    if (!this.state.isFocused) {
      this.props.onFocus?.(e);
      this.setState({ isFocused: true });
    }
  };

  _onKeyDown = (event) => {
    const { key } = event;
    if (key === 'ArrowRight' || key === 'ArrowLeft') {
      return;
    }
    event.stopPropagation();
    const { closeWhenChildrenClicked } = this.props;
    const { isOpen, rowHovered, searchString, searchTimeout } = this.state;
    const children = this._getMenuItemsWithoutGroups();

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (key === 'Escape') {
      if (isOpen) {
        this._onClose();
      }
    } else if (key === 'Tab') {
      if (isOpen) {
        event.preventDefault();
      }
    } else {
      if ((key === 'ArrowDown' || key === 'ArrowUp') && !isOpen) {
        this.setState({ isOpen: true });
      }

      if (key === 'ArrowDown') {
        event.preventDefault();

        if (rowHovered === null) {
          this.setState({ rowHovered: this._getNextEnabledIndex(0) });
        } else {
          this.setState({
            rowHovered: this._getNextEnabledIndex(rowHovered === children.length - 1 ? 0 : rowHovered + 1),
          });
        }

        this._clearSearchString();
        this._clearTimeout();
      } else if (key === 'ArrowUp') {
        event.preventDefault();

        if (rowHovered === null) {
          this.setState({
            rowHovered: this._getPreviousEnabledIndex(children.length - 1),
          });
        } else {
          this.setState({
            rowHovered: this._getPreviousEnabledIndex(rowHovered === 0 ? children.length - 1 : rowHovered - 1),
          });
        }

        this._clearSearchString();
        this._clearTimeout();
      } else if (key === ' ' && !isOpen && searchTimeout === null) {
        event.preventDefault();
        this._onOpen();
      } else if (key === ' ' && rowHovered !== null && searchTimeout === null) {
        const { onClick } = children[rowHovered].props;

        if (onClick) {
          onClick();
        }

        if (closeWhenChildrenClicked) {
          this._onClose();
        }
      } else if (key === 'Enter' && rowHovered !== null) {
        const { onClick } = children[rowHovered].props;

        if (onClick) {
          onClick();
        }

        if (closeWhenChildrenClicked) {
          setTimeout(() => this._onClose(), 1);
        }
      } else {
        event.preventDefault();

        const newSearchString = searchString + event.key;
        const index = this._getMatchingChildIndex(newSearchString);

        this._clearTimeout();
        this._setTimeout();

        if (!isOpen) {
          const prevIndex = this._getMatchingChildIndex(searchString);

          if (prevIndex !== index) {
            const child = this._getMenuItemsWithoutGroups()[index];

            if (child && child.props && child.props.onClick) {
              child.props.onClick();
            }
          }
        } else if (index !== null && index !== rowHovered) {
          this.setState({
            rowHovered: index,
          });
        }

        this.setState({
          searchString: newSearchString,
        });
      }
    }
  };

  _onOpen = () => {
    const rowHoveredIndex = this._getMenuItemsWithoutGroups().findIndex((item) => !!item.props.selected);
    const rowHovered = rowHoveredIndex === -1 ? null : rowHoveredIndex;

    this.setState({ isOpen: true, rowHovered });
    this.props.onOpen();
  };

  _onRowHover = (rowHovered = null) => {
    this.setState({ rowHovered });
  };

  _setTimeout() {
    const searchTimeout = setTimeout(() => {
      this._clearSearchString();
      this._clearTimeout();
    }, TYPEAHEAD_TIMEOUT);

    this.setState({ searchTimeout });
  }

  _getDropdownMenuId = () => {
    const { id, dropdownMenuType } = this.props;

    if (id) {
      if (dropdownMenuType === DropdownMenuType.ComboBox) {
        return `${id}-listbox`;
      }

      if (dropdownMenuType === DropdownMenuType.Menu) {
        return `${id}-menu`;
      }
    }
  };

  _getDropdownMenuRole = () => {
    const { dropdownMenuType } = this.props;

    if (dropdownMenuType === DropdownMenuType.ComboBox) {
      return AriaWidgetRole.ListBox;
    }

    if (dropdownMenuType === DropdownMenuType.Menu) {
      return AriaWidgetRole.Menu;
    }
  };

  _renderButton = () => {
    const { disabled, disabledTooltipMessage, hasError, inlineMessageType, variant, dropdownMenuType } = this.props;

    return React.cloneElement(this._getMenuToggler(), {
      chevronWrapperClassName: style['dropdown-toggler-chevron-icon-wrapper'],
      disabled,
      disabledTooltipMessage,
      hasError,
      inlineMessageType,
      variant,
      dropdownMenuType,
      dropdownMenuId: this._getDropdownMenuId(),
    });
  };

  _renderDropdownMenu = () => {
    const { dropdownMenuClassName, fullWidth, fullHeight, dropdownMenuType } = this.props;
    const { rowHovered } = this.state;
    const dropdownMenuClasses = classnames(style['inner-dropdown-menu'], dropdownMenuClassName, {
      [style['full-width']]: fullWidth,
      [style['full-height']]: fullHeight,
    });
    let actualIndex = 0;

    const dropdownMenuId = this._getDropdownMenuId();
    const dropdownMenuRole = this._getDropdownMenuRole();

    return (
      <div
        className={dropdownMenuClasses}
        id={dropdownMenuId}
        role={dropdownMenuRole}
        {...generateDataTestidAttribute('inner-dropdown-menu')}
      >
        {this._getMenuItems().map((child) => {
          if (typeof child.type === 'string') {
            return React.cloneElement(child, { index: actualIndex++ });
          }

          if (child.type === dropdownMenuItemGroupType) {
            const startingIndexForGroup = actualIndex;

            actualIndex += this._getMenuItems(child.props.children).length;

            return React.cloneElement(child, {
              index: startingIndexForGroup,
              onRowHover: this._onRowHover,
              rowHovered,
              dropdownMenuType,
            });
          }

          return React.cloneElement(child, {
            hovered: actualIndex === rowHovered,
            index: actualIndex++,
            onRowHover: this._onRowHover,
            dropdownMenuType,
          });
        })}
      </div>
    );
  };

  render() {
    const {
      ariaLabel,
      className,
      closeWhenChildrenClicked,
      containerAllowedMeToClose,
      dropdownMenuShouldHaveWidthOfButton,
      fullWidth,
      inlineMessage,
      inlineMessageTooltip,
      inlineMessageType,
      id,
      menuPosition,
    } = this.props;
    const { isOpen } = this.state;
    const toggler = this._getMenuToggler();
    const onBlur = callbackFunctionDecorator(this._onBlur, toggler?.props?.onBlur);
    const onFocus = callbackFunctionDecorator(this._onFocus, toggler?.props?.onFocus);

    return (
      <>
        <Dropdown
          ref={this._dropdownRef}
          className={classnames(style['dropdown-menu'], { [style['full-width']]: fullWidth }, className)}
          closeWhenChildrenClicked={closeWhenChildrenClicked}
          containerAllowedMeToClose={containerAllowedMeToClose}
          {...generateDataTestidAttribute(this.props['data-testid'])}
          ariaLabel={ariaLabel}
          dropdownMenuShouldHaveWidthOfButton={dropdownMenuShouldHaveWidthOfButton}
          id={id}
          isOpen={isOpen}
          menuPosition={menuPosition}
          onBlur={onBlur}
          onClose={this._onClose}
          onFocus={onFocus}
          onKeyDown={this._onKeyDown}
          onOpen={this._onOpen}
          onScroll={this._onScrollPropogationHandler}
          openClassName={style.open}
          openTogglerClassName='selected'
        >
          {this._renderButton()}
          {this._renderDropdownMenu()}
        </Dropdown>
        {inlineMessage && (
          <InlineMessage tooltipContent={inlineMessageTooltip} type={inlineMessageType}>
            {inlineMessage}
          </InlineMessage>
        )}
      </>
    );
  }
}

export default DropdownMenu;
